import React, { useState, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bold, Italic, Strikethrough, Code, List, ListOrdered, 
  Link, ImagePlus, Heading1, Heading2, Undo, Redo, PenTool,
  Move3D, Table, MessageSquare, Smile, Type, Highlighter
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DashboardModal from '@/components/dashboard-modal';
import { InsertLinkDialog } from './components/InsertLinkDialog';
import { InsertImageDialog } from './components/InsertImageDialog';
import { InsertWorkbookImageDialog } from './components/InsertWorkbookImageDialog';
import { InsertAITextDialog } from './components/InsertAITextDialog';
import { EditorChatPanel } from './components/EditorChatPanel';
import { EmojiPicker } from './components/EmojiPicker';
import { ColorPicker } from './components/ColorPicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { MobileEditorToolbar } from './MobileEditorToolbar';

interface EditorToolbarProps {
  editor: Editor;
  workbookId?: string;
  pageId?: string;
}

export function EditorToolbar({ 
  editor, 
  workbookId, 
  pageId
}: EditorToolbarProps) {
  const { isMobile } = useIsMobile();
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  
  // Stable emoji picker state management
  const handleEmojiPickerToggle = useCallback((open: boolean) => {
    setEmojiPickerOpen(open);
  }, []);

  // Stable emoji selection handler - keeps dropdown open for multiple selections
  const handleEmojiSelect = useCallback((emoji: string) => {
    try {
      // Ensure editor is available and focused
      if (!editor) return;
      
      editor.chain().focus().insertContent(emoji).run();
      // Keep dropdown open for multiple emoji selections
    } catch (error) {
      console.error('[EditorToolbar] Error inserting emoji:', error);
      // Don't close on error either - user can try again
    }
  }, [editor]);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [backgroundColorPickerOpen, setBackgroundColorPickerOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Auto-open AI Text Magic modal when triggered from AI Agent
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenTextMagic = searchParams.get('openTextMagic') === 'true';
    
    if (shouldOpenTextMagic && workbookId && pageId) {
      console.log('[EditorToolbar] Auto-opening AI Text Magic modal');
      setTextModalOpen(true);
      
      // Clean up URL parameter
      searchParams.delete('openTextMagic');
      const newUrl = searchParams.toString() 
        ? `${location.pathname}?${searchParams.toString()}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, workbookId, pageId, navigate]);
  
  if (!editor) return null;
  
  // Handle link insertion
  const handleInsertLink = () => {
    if (!linkUrl) return;
    
    // If there's no text selected and link text is provided, insert text with link
    if (linkText && !editor.state.selection.content().size) {
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${linkUrl}">${linkText}</a>`)
        .run();
    } else {
      // Otherwise, convert the selection to a link
      editor
        .chain()
        .focus()
        .setLink({ href: linkUrl })
        .run();
    }
    
    setLinkModalOpen(false);
    setLinkUrl('');
    setLinkText('');
  };
  
  // Handle link removal
  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setLinkModalOpen(false);
  };
  
  // Handle image insertion
  const handleInsertImage = () => {
    if (!imageUrl) return;
    
    editor
      .chain()
      .focus()
      .setImage({ 
        src: imageUrl,
        alt: imageAlt 
      })
      .run();
    
    setImageModalOpen(false);
    setImageUrl('');
    setImageAlt('');
  };
  
  // Open link modal with current selection if editing an existing link
  const openLinkModal = () => {
    setLinkUrl('');
    setLinkText('');
    
    // Check if we're editing an existing link
    if (editor.isActive('link')) {
      const linkAttrs = editor.getAttributes('link');
      setLinkUrl(linkAttrs.href || '');
    }
    
    // Get selected text for the link text
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    setLinkText(selectedText || '');
    
    setLinkModalOpen(true);
  };
  
  // TODO: RESTORE AI IMAGE MAGIC BUTTON - Change `false` to `true` below to restore the purple AI Image button
  const showAiImageButton = true;

  // Return mobile toolbar for mobile devices
  if (isMobile) {
    return (
      <>
        <MobileEditorToolbar
          editor={editor}
          onOpenTextModal={() => setTextModalOpen(true)}
          onOpenImageModal={() => setImageModalOpen(true)}
          onOpenLinkModal={openLinkModal}
          onInsertEmoji={handleEmojiSelect}
          workbookId={workbookId}
          pageId={pageId}
        />
        
        {/* Modals - shared between mobile and desktop */}
        <DashboardModal
          isOpen={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          title="Add Link"
          size="md"
          accentColor="blue"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URL</label>
              <Input
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Link Text (optional)</label>
              <Input
                type="text"
                placeholder="Link text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
              />
            </div>
            
            {/* Inline Footer Buttons */}
            <div className="flex justify-end items-center gap-2 pt-4">
              <Button variant="outline" onClick={() => setLinkModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleInsertLink} disabled={!linkUrl.trim()}>
                Add Link
              </Button>
            </div>
          </div>
        </DashboardModal>
        
        {/* AI Text Modal */}
        <InsertAITextDialog
          editor={editor}
          isOpen={textModalOpen}
          onClose={() => setTextModalOpen(false)}
          workbookId={workbookId}
          pageId={pageId}
        />

        {/* Image Modal */}
        <InsertWorkbookImageDialog
          editor={editor}
          isOpen={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          workbookId={workbookId}
          pageId={pageId}
        />
        
        {/* Chat Panel */}
        <EditorChatPanel
          isOpen={chatPanelOpen}
          onClose={() => setChatPanelOpen(false)}
          workbookId={workbookId}
          pageId={pageId}
        />
      </>
    );
  }

  // Desktop toolbar
  return (
    <div className="border-b p-2 bg-white dark:bg-gray-900 flex flex-wrap items-center gap-1">
      {/* 🪄 MAGICAL AI TEXT BUTTON - Now on the left! */}
      <div className="relative mr-3">
        <button
          onClick={() => setTextModalOpen(true)}
          className="relative group inline-flex items-center justify-center h-10 w-10 rounded-xl text-sm font-medium 
                     bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 
                     hover:from-purple-500 hover:via-purple-600 hover:to-violet-700
                     text-white shadow-2xl hover:shadow-3xl hover:shadow-purple-500/40
                     transform hover:scale-110 active:scale-95 transition-all duration-500 ease-out
                     ring-4 ring-purple-200/50 dark:ring-purple-800/50 hover:ring-purple-300/70 dark:hover:ring-purple-700/70
                     before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/20 before:to-transparent 
                     before:opacity-0 before:transition-all before:duration-500 hover:before:opacity-100
                     after:absolute after:inset-0 after:rounded-xl after:bg-gradient-to-br after:from-transparent after:to-purple-900/20
                     border border-purple-300/30 hover:border-purple-400/50"
          aria-label="AI Text Generation"
          title="✨ AI Text Magic - Generate Content"
          data-test-id="editor-text-button"
        >
          {/* Main icon with clean styling */}
          <div className="relative z-10 flex items-center justify-center">
            <PenTool className="h-5 w-5 drop-shadow-lg filter brightness-110" />
          </div>
          
          {/* Clean magical aura effect */}
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-500/30 via-purple-600/20 to-violet-700/30 
                          opacity-0 group-hover:opacity-100 blur-md scale-125 group-hover:scale-150 
                          transition-all duration-700 ease-out pointer-events-none"></div>
        </button>
      </div>

      {/* 🪄 MAGICAL AI IMAGE BUTTON - Now on the right of text button */}
      {showAiImageButton && (
        <div className="relative mr-3">
          <button
            onClick={() => setImageModalOpen(true)}
            className="relative group inline-flex items-center justify-center h-10 w-10 rounded-xl text-sm font-medium 
                       bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 
                       hover:from-purple-500 hover:via-purple-600 hover:to-violet-700
                       text-white shadow-2xl hover:shadow-3xl hover:shadow-purple-500/40
                       transform hover:scale-110 active:scale-95 transition-all duration-500 ease-out
                       ring-4 ring-purple-200/50 dark:ring-purple-800/50 hover:ring-purple-300/70 dark:hover:ring-purple-700/70
                       before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/20 before:to-transparent 
                       before:opacity-0 before:transition-all before:duration-500 hover:before:opacity-100
                       after:absolute after:inset-0 after:rounded-xl after:bg-gradient-to-br after:from-transparent after:to-purple-900/20
                       border border-purple-300/30 hover:border-purple-400/50"
            aria-label="AI Image Generation"
            title="✨ AI Image Magic - Generate or Upload"
            data-test-id="editor-image-button"
          >
            {/* Main icon with clean styling */}
            <div className="relative z-10 flex items-center justify-center">
              <ImagePlus className="h-5 w-5 drop-shadow-lg filter brightness-110" />
            </div>
            
            {/* Clean magical aura effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-500/30 via-purple-600/20 to-violet-700/30 
                            opacity-0 group-hover:opacity-100 blur-md scale-125 group-hover:scale-150 
                            transition-all duration-700 ease-out pointer-events-none"></div>
          </button>
        </div>
      )}
      
      {/* 😀 EMOJI PICKER - Stable DropdownMenu implementation */}
      <DropdownMenu open={emojiPickerOpen} onOpenChange={handleEmojiPickerToggle}>
        <DropdownMenuTrigger asChild>
          <Toggle
            size="sm"
            pressed={emojiPickerOpen}
            aria-label="Insert Emoji"
            title="Insert Emoji"
            data-testid="emoji-picker-button"
            data-button-type="emoji-picker"
            role="button"
          >
            <Smile className="h-4 w-4" />
          </Toggle>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          className="w-auto p-0 border-0 shadow-2xl z-50" 
          align="start"
          side="bottom"
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={8}
        >
          <div data-emoji-picker className="focus:outline-none">
            <EmojiPicker
              onEmojiSelect={handleEmojiSelect}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('bold')}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('italic')}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('strike')}
        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
        aria-label="Strikethrough"
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Toggle>
      
      <Separator orientation="vertical" className="h-6" />
      
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 1 })}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        aria-label="Heading 1"
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 2 })}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        aria-label="Heading 2"
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-6" />
      
      <Toggle
        size="sm"
        pressed={editor.isActive('bulletList')}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bullet List"
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('orderedList')}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        aria-label="Ordered List"
        title="Ordered List"
      >
        <ListOrdered className="h-4 w-4" />
      </Toggle>
      
      <Separator orientation="vertical" className="h-6" />
      
      {/* Text Color Picker */}
      <Popover open={textColorPickerOpen} onOpenChange={setTextColorPickerOpen}>
        <PopoverTrigger asChild>
          <Toggle
            size="sm"
            pressed={textColorPickerOpen || editor.isActive('textStyle')}
            aria-label="Text Color"
            title="Text Color"
            className="data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
          >
            <Type className="h-4 w-4" />
          </Toggle>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0 border-0 shadow-2xl" 
          align="start"
          side="bottom"
          sideOffset={8}
        >
          <ColorPicker
            type="text"
            onColorSelect={(color: string) => {
              if (color === '#000000' || color === '#FFFFFF') {
                editor.chain().focus().unsetColor().run();
              } else {
                editor.chain().focus().setColor(color).run();
              }
              setTextColorPickerOpen(false);
            }}
            currentColor={editor.getAttributes('textStyle').color}
          />
        </PopoverContent>
      </Popover>
      
      {/* Background Color Picker */}
      <Popover open={backgroundColorPickerOpen} onOpenChange={setBackgroundColorPickerOpen}>
        <PopoverTrigger asChild>
          <Toggle
            size="sm"
            pressed={backgroundColorPickerOpen || editor.isActive('highlight')}
            aria-label="Background Color"
            title="Background Color"
            className="data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
          >
            <Highlighter className="h-4 w-4" />
          </Toggle>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0 border-0 shadow-2xl" 
          align="start"
          side="bottom"
          sideOffset={8}
        >
          <ColorPicker
            type="background"
            onColorSelect={(color: string) => {
              if (color === 'transparent') {
                editor.chain().focus().unsetHighlight().run();
              } else {
                editor.chain().focus().setHighlight({ color }).run();
              }
              setBackgroundColorPickerOpen(false);
            }}
            currentColor={editor.getAttributes('highlight').color}
          />
        </PopoverContent>
      </Popover>
      
      <Separator orientation="vertical" className="h-6" />
      
      <Toggle
        size="sm"
        pressed={editor.isActive('table')}
        onPressedChange={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        aria-label="Insert Table"
        title="Insert Table"
      >
        <Table className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('code')}
        onPressedChange={() => editor.chain().focus().toggleCode().run()}
        aria-label="Code"
        title="Code"
      >
        <Code className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('link')}
        onPressedChange={openLinkModal}
        aria-label="Link"
        title="Link"
        data-test-id="editor-link-button"
      >
        <Link className="h-4 w-4" />
      </Toggle>
      
      <div className="ml-auto flex items-center gap-1">
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          aria-label="Undo"
          title="Undo"
        >
          <Undo className="h-4 w-4" />
        </Toggle>
        
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          aria-label="Redo"
          title="Redo"
        >
          <Redo className="h-4 w-4" />
        </Toggle>
        
        <Separator orientation="vertical" className="h-6 mx-2" />
        
        {/* 💬 CHAT BUTTON - TEMPORARILY HIDDEN - Clean and Minimalist */}
        {/* 
        TODO: RE-ENABLE EDITOR CHAT BUTTON - Remove the "hidden" class below to restore the blue chat button
        <button
          onClick={() => setChatPanelOpen(true)}
          className="relative group inline-flex items-center justify-center h-8 w-8 rounded-md text-sm font-medium 
                     bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 
                     hover:from-blue-400 hover:via-blue-500 hover:to-blue-600
                     text-white shadow-md hover:shadow-lg hover:shadow-blue-500/25
                     transform hover:scale-105 active:scale-95 transition-all duration-200 ease-out
                     border border-blue-300/30 hover:border-blue-400/50"
          aria-label="Open Editor Chat"
          title="💬 Editor Chat - Get AI help with your content"
          data-test-id="editor-chat-button"
        >
          <MessageSquare className="h-4 w-4" />
        </button>
        */}
      </div>
      
      {/* Link Modal */}
      <DashboardModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        title="Add Link"
        size="md"
        accentColor="blue"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">URL</label>
            <Input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Link Text (optional)</label>
            <Input
              type="text"
              placeholder="Link text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
            />
          </div>
          
          {/* Inline Footer Buttons */}
          <div className="flex justify-end items-center gap-2 pt-4">
            <Button variant="outline" onClick={() => setLinkModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInsertLink} disabled={!linkUrl.trim()}>
              Add Link
            </Button>
          </div>
        </div>
      </DashboardModal>
      
      {/* AI Text Modal */}
      <InsertAITextDialog
        editor={editor}
        isOpen={textModalOpen}
        onClose={() => setTextModalOpen(false)}
        workbookId={workbookId}
        pageId={pageId}
      />

      {/* Image Modal - ALWAYS show advanced modal */}
      <InsertWorkbookImageDialog
        editor={editor}
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        workbookId={workbookId}
        pageId={pageId}
      />
      


      {/* Chat Panel */}
      <EditorChatPanel
        isOpen={chatPanelOpen}
        onClose={() => setChatPanelOpen(false)}
        workbookId={workbookId}
        pageId={pageId}
      />
    </div>
  );
}
